/**
 * APP-CONTEXT-DESK-1 — WHAT this app says the person has open, by REFERENCE only (`AI_APP_ROUTE { generation, path, resource? }`): a kind and
 * a ref, never a title or any content. THE SAME words as the host's `validAppResource` (`apps/vitto-app/src/embed/app-resource.ts` and the
 * door's `AppResource` in `APIs/vitto-api/src/infra/http/ui/client-context.ts`): the app validates before it sends, the host validates
 * again, the door once more — none trusts the others.
 *
 * `kind` is a CLOSED list (growing it is a change in the host AND at the door, on purpose). `ref` is a bounded token with a closed
 * alphabet — no space, slash, backslash, quote or control character: a reference, not prose. A hint, never authority: Vitto resolves the
 * ref with this app's own read tool under the person's role (`task.get`, `board.read`).
 */
export const APP_RESOURCE_KINDS = ['task', 'board'] as const;
export type AppResourceKind = (typeof APP_RESOURCE_KINDS)[number];
export const APP_RESOURCE_REF_MAX = 128;
export const APP_RESOURCE_REF_RE = /^[A-Za-z0-9._:-]{1,128}$/;

export type AppResourceHint = { kind: AppResourceKind; ref: string };

/** EXACTLY `{ kind, ref }` (no other key), `kind` from the closed list, `ref` a bounded reference. A fresh object of the two, or null. */
export function validAppResource(value: unknown): AppResourceHint | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;
  const keys = Object.keys(value);
  if (keys.length !== 2 || !keys.includes('kind') || !keys.includes('ref')) return null;
  const { kind, ref } = value as { kind: unknown; ref: unknown };
  if (typeof kind !== 'string' || !(APP_RESOURCE_KINDS as readonly string[]).includes(kind))
    return null;
  if (typeof ref !== 'string' || !APP_RESOURCE_REF_RE.test(ref)) return null;
  return { kind: kind as AppResourceKind, ref };
}
